import SeeWardLogo from '../../../assets/image/logoSeeWard.png';
import {
  useEffect,
  useState
} from 'react';
import workspaceService, {
  getWorkspaceOnboarding
} from '../../../service/workspace.service';
import {
  Bug,
  Plus,
  Server,
  Trash2,
  UsersRound
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useDispatch,
  useSelector
} from 'react-redux';
import {
  selectCurrentEmail,
  selectCurrentWorkspace
} from '../../../redux/auth/selectors';
import {
  fetchUser,
  updateDefaultWorkspace
} from '../../../redux/auth/actions';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Empty,
  Input,
  notification,
  Popconfirm,
  Tooltip
} from 'antd';
import {
  acceptMyInvitation,
  declineMyInvitation,
  getMyInvitations
} from '../../../service/invitation.service';
import {
  invitationIssuer,
  invitationOffer
} from '../../../utils/invitations';
import { emitWorkspacesChanged } from '../../../utils/workspaceEvents';
import {
  PLATFORM_ACCESS_PENDING_MESSAGE,
  refreshAfterPlatformGrant
} from '../../../utils/platformAccess';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import DeleteWorkspaceModal from './DeleteWorkspaceModal';
import WorkspaceAccountMenu from './WorkspaceAccountMenu';

const { Search } = Input;

export default function Example() {
  const email = useSelector(selectCurrentEmail);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [invitationError, setInvitationError] = useState(false);
  const [invitationsLoaded, setInvitationsLoaded] = useState(false);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [invitationLoading, setInvitationLoading] = useState<Record<string, boolean>>({});
  const [workspaceToDelete, setWorkspaceToDelete] = useState<any | null>(null);
  const [query, setQuery] = useState('');
  const activeWorkspace = useSelector(selectCurrentWorkspace);

  // Returns the list on a successful read and `null` when the read itself failed — the
  // two are not the same thing. `getWorkspaceList` swallows its own errors and hands back
  // errorHandler's `{success:false,…}` object, which used to land here as `[]` and empty
  // the picker, telling the user they belong to no workspace on the strength of a request
  // that never answered. Keep what is on screen instead, and let the callers that act on
  // a workspace's *absence* tell a failed read apart from a genuinely empty one.
  const fetchWorkspaces = async (query = ''): Promise<any[] | null> => {
    try {
      setLoading(true);
      setLoadError(false);
      const res = await workspaceService.getWorkspaceList(query);
      if (!Array.isArray(res)) {
        setLoadError(true);
        return null;
      }
      setDataSource(res);
      return res;
    } catch (error) {
      setLoadError(true);
      console.error('Failed to fetch workspaces:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const res = await getMyInvitations();
      setInvitationError(!Array.isArray(res));
      if (Array.isArray(res)) setInvitations(res);
    } catch {
      setInvitationError(true);
    } finally {
      setInvitationsLoaded(true);
    }
  };

  useEffect(() => {
    fetchWorkspaces();
    fetchInvitations();
    getWorkspaceOnboarding().then((result) => {
      setEmailVerificationRequired(result.email_verification_required === true);
    }).catch(() => { /* Workspace retry remains available when the API is down. */ });
  }, []);

  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { canAccessPlatformAdmin } = useCurrentUser();

  // A platform account never holds a workspace membership, so with nothing to open and
  // nothing to accept this picker is a dead end; the console is the only place it can go.
  // Both reads must have answered cleanly: a failed read says nothing about emptiness.
  useEffect(() => {
    if (
      canAccessPlatformAdmin &&
      !loading && !loadError && !query && dataSource.length === 0 &&
      invitationsLoaded && !invitationError && invitations.length === 0
    ) {
      navigate('/admin', { replace: true });
    }
  }, [canAccessPlatformAdmin, loading, loadError, query, dataSource, invitationsLoaded, invitationError, invitations, navigate]);

  // api() reads the active workspace straight back out of localStorage to build the
  // x-workspace-id header, so redux alone is not enough — both have to move together,
  // and `null` has to be a real state (the user may now belong to no workspace at all).
  const persistActiveWorkspace = async (workspace: any | null) => {
    await dispatch(updateDefaultWorkspace(workspace));
    const existingAuth = JSON.parse(sessionStorage.getItem('auth') || '{}');
    sessionStorage.setItem('auth', JSON.stringify({
      ...existingAuth,
      current: {
        ...existingAuth.current,
        default_workspace: workspace
      }
    }));
  };

  const onSelectWorkspace = async (workspace: any) => {
    try {
      await persistActiveWorkspace(workspace);
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to select workspace:', error);
    } finally {
      setLoading(false);
    }
  };

  const isWorkspaceRow = (w: any) => (w?.kind ?? 'workspace') === 'workspace';
  const createFrom = dataSource.find((workspace) => isWorkspaceRow(workspace) && workspace.can_create_workspace);

  // The list now carries organisations and entities too. Only workspaces get a card; when
  // the backend sends hierarchy rows they are grouped under their entity. An old backend
  // sends none, and the cards render ungrouped as before.
  const workspaceRows = dataSource.filter((n) => (n.kind ?? 'workspace') === 'workspace');
  const hasHierarchy = dataSource.some((n) => n.kind === 'entity' || n.kind === 'organisation');
  const cardRows: { header?: string; product?: any }[] = (() => {
    if (!hasHierarchy) return workspaceRows.map((product) => ({ product }));
    const groups = new Map<string, any[]>();
    workspaceRows.forEach((product) => {
      const entity = dataSource.find((n) => n.kind === 'entity' && n.id === product.entity_id);
      const title = entity?.name ?? 'Other';
      groups.set(title, [...(groups.get(title) ?? []), product]);
    });
    return [...groups.keys()]
      .sort((a, b) => a.localeCompare(b))
      .flatMap((title) => [
        { 
          header: title 
        }, ...(groups.get(title) ?? []).map((product) => ({ 
          product 
        }))
      ]);
  })();
  const onCreateWorkspace = async () => {
    if (!createFrom) return;
    await persistActiveWorkspace(createFrom);
    navigate('/workspace/create');
  };

  // Promote a replacement only if the user still has one to be promoted to; with zero
  // workspaces left they stay here, on the picker, and get the empty state below.
  const promoteReplacement = async (remaining: any[]) => {
    const replacement = remaining.find(isWorkspaceRow);
    if (!replacement) return;
    await persistActiveWorkspace(replacement);
    await workspaceService.setDefaultWorkspace(replacement.id);
    localStorage.setItem('server_default_ws_id', String(replacement.id));
  };

  const onWorkspaceDeleted = async (deleted: any) => {
    const wasActive = String(activeWorkspace?.id) === String(deleted.id);

    // Clear the dead id *before* the refetch, so no request goes out carrying an
    // x-workspace-id that now 404s. The backend has already nulled the active
    // workspace on its side and does not choose a replacement for us.
    if (wasActive) {
      await persistActiveWorkspace(null);
    }
    if (localStorage.getItem('server_default_ws_id') === String(deleted.id)) {
      localStorage.removeItem('server_default_ws_id');
    }

    // Refetch rather than removing the row optimistically — the row is simply absent
    // from the list now (`is_deleted` is a dead legacy field and is never set).
    const remaining = await fetchWorkspaces();

    if (wasActive) {
      await promoteReplacement(remaining ?? []);
    }

    emitWorkspacesChanged();
    setWorkspaceToDelete(null);
  };

  // The DELETE came back 404, which under §4.1 means "no workspace with this id that you
  // can reach" — either it is already gone, or it is still there and no longer yours. The
  // 404 alone establishes neither, so nothing here is torn down on the strength of it:
  // re-read the list first and let *that* decide, since it is the same list the picker is
  // showing and the same membership the delete was refused against.
  const onWorkspaceUnavailable = async (unavailable: any) => {
    setWorkspaceToDelete(null);

    const remaining = await fetchWorkspaces(query);

    if (remaining === null) {
      notification.warning({
        message: 'Could not confirm the outcome',
        description: `${unavailable.name} was not deleted, and the workspace list could not be re-read to check its current state. Reload the page to see where things stand.`
      });
      return;
    }

    if (remaining.some((w: any) => String(w.id) === String(unavailable.id))) {
      notification.warning({
        message: 'Workspace not deleted',
        description: `${unavailable.name} is still here and was not deleted. Your permission to delete it may have been withdrawn.`
      });
      return;
    }

    // Absent from the caller's own list too. Whatever the cause — deleted by someone else,
    // or membership revoked — the id is no longer usable, so drop it from the session.
    // It is this second, independent read that authorises that, not the 404.
    const wasActive = String(activeWorkspace?.id) === String(unavailable.id);
    if (wasActive) {
      await persistActiveWorkspace(null);
    }
    if (localStorage.getItem('server_default_ws_id') === String(unavailable.id)) {
      localStorage.removeItem('server_default_ws_id');
    }
    if (wasActive) {
      await promoteReplacement(remaining);
    }

    notification.info({
      message: 'Workspace no longer available',
      description: `${unavailable.name} is no longer available to you, so it could not be deleted. It has been removed from your list.`
    });

    emitWorkspacesChanged();
  };

  const onSearch = (value: string) => {
    setQuery(value);
    fetchWorkspaces(value);
  };

  const setInvitationBusy = (invitationId: number, busy: boolean) =>
    setInvitationLoading((prev) => ({
      ...prev,
      [String(invitationId)]: busy
    }));

  // Accepting changes what this person can reach, so everything that shows it is re-read in
  // place: the inbox (the invitation is gone), the workspace list (the new access is there to
  // pick), and the session (capabilities and, for a support admin invitation, the platform
  // role). There is no reload: the picker is where the new workspace is chosen.
  const onAcceptInvitation = async (invitationId: number) => {
    setInvitationBusy(invitationId, true);
    try {
      const offered = invitations.find((inv: any) => inv.id === invitationId);
      const accepted = await acceptMyInvitation(invitationId);
      const grantsPlatform = Boolean(accepted?.platform_role || offered?.platform_role);
      // A support admin role is a Keycloak role, resolved per access token: renew the token
      // before reading the profile, or `/me` keeps reporting the roles the old one had.
      const platformReady = grantsPlatform
        ? await refreshAfterPlatformGrant(dispatch)
        : false;
      if (!grantsPlatform) await dispatch(fetchUser());
      emitWorkspacesChanged();
      await Promise.all([fetchInvitations(), fetchWorkspaces(query)]);
      notification.success({ message: 'Invitation accepted.' });
      if (platformReady) {
        navigate('/admin');
      } else if (grantsPlatform) {
        notification.info({ message: PLATFORM_ACCESS_PENDING_MESSAGE });
      }
    } catch (error: any) {
      notification.error({
        message: error?.response?.data?.detail || 'The invitation could not be accepted.'
      });
      // The invitation may have lapsed or been withdrawn meanwhile: show what is true now.
      await fetchInvitations();
    } finally {
      setInvitationBusy(invitationId, false);
    }
  };

  const onDeclineInvitation = async (invitationId: number) => {
    setInvitationBusy(invitationId, true);
    try {
      await declineMyInvitation(invitationId);
      await fetchInvitations();
      notification.success({ message: 'Invitation declined.' });
    } catch (error: any) {
      notification.error({
        message: error?.response?.data?.detail || 'The invitation could not be declined.'
      });
      await fetchInvitations();
    } finally {
      setInvitationBusy(invitationId, false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <nav
          aria-label="Global"
          className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8"
        >
          <div className="flex lg:flex-1">
            <a
              href="#"
              className="-m-1.5 p-1.5"
            >
              <span className="sr-only">SeeWard</span>
              <img
                alt=""
                src={SeeWardLogo}
                className="h-10 w-auto"
              />
            </a>
          </div>
          <div className="hidden lg:flex lg:gap-x-12" />
          <div className="flex flex-1 justify-end">
            <WorkspaceAccountMenu />
          </div>
        </nav>
      </header>

      <main className="px-6 pb-12 lg:px-8">
        <div className="mx-auto max-w-7xl py-12">
          <div className="max-w-2xl">
            <h1
              className="text-balance text-3xl font-semibold tracking-tight text-slate-950 sm:text-5xl"
            >
              Welcome to Seeward
            </h1>
            <p className="mt-3 text-base text-slate-600">
              Choose a workspace to get started, or accept an invitation from your team.
            </p>
            {/* A platform account may have no workspace at all; this page is its only menu. */}
            {canAccessPlatformAdmin && (
              <Button
                className="mt-4"
                type="primary"
                onClick={() => navigate('/admin')}
              >Open platform console</Button>
            )}
          </div>
        </div>
        <div className="mx-auto max-w-7xl">

          {/* Pending Invitations */}
          {invitationError && (
            <Alert
              type="warning"
              showIcon
              message="We could not load your invitations."
              action={<Button
                onClick={fetchInvitations}
              >Retry invitations</Button>} /
            >
          )}
          {invitations.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <h2
                  className="text-2xl font-medium text-gray-900"
                >Pending Invitations</h2>
                <Badge
                  count={invitations.length}
                  color="orange"
                />
              </div>
              <div
                className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-3"
              >
                {invitations.map((inv: any) => (
                  <div
                    key={inv.id}
                    className="overflow-hidden flex flex-col justify-between rounded-xl border border-amber-200 bg-white shadow-sm"
                  >
                    <div className="px-4 py-5 sm:p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div
                            className="text-lg font-semibold"
                          >{invitationIssuer(inv)}</div>
                          <div className="text-sm text-[#6B7280] mt-1">
                            {inv.platform_role ? 'Access:' : 'Access to:'}
                            {' '}
                            <span
                              className="font-medium text-gray-800"
                            >{invitationOffer(inv)}</span>
                          </div>
                        </div>
                        <span
                          className="shrink-0 text-xs font-medium bg-orange-100 text-orange-700 px-2 py-1 rounded-full"
                        >
                          Invited
                        </span>
                      </div>
                    </div>
                    <div
                      className="px-4 py-3 sm:px-4 border-t border-gray-100 flex gap-2 justify-end"
                    >
                      <Popconfirm
                        title="Decline this invitation?"
                        description="No access will be granted. The organisation would have to invite you again."
                        okText="Decline"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDeclineInvitation(inv.id)}
                      >
                        <Button
                          size="small"
                          disabled={invitationLoading[String(inv.id)]}
                        >
                          Decline
                        </Button>
                      </Popconfirm>
                      <Button
                        type="primary"
                        size="small"
                        loading={invitationLoading[String(inv.id)]}
                        onClick={() => onAcceptInvitation(inv.id)}
                      >
                        Accept
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Workspaces */}
          <div className="flex items-center justify-between space-x-4">
            <h2 className="text-2xl font-medium text-gray-900">Workspaces</h2>
            <div className="max-w-xl w-full">
              <Search
                placeholder="Search Workspace"
                loading={false}
                allowClear
                size='large'
                onSearch={onSearch}
              />
            </div>
          </div>
          {loading && (
            <div className='bg-transparent flex justify-center mt-10'>
              <div
                className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent" /
              >
            </div>
          )}
          {!loading && (
            <div
              className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
            >
              {createFrom && (
                <div
                  onClick={onCreateWorkspace}
                  className="min-h-[220px] rounded-xl border border-dashed border-blue-300 bg-blue-50 p-1 font-semibold hover:bg-blue-100"
                >
                  <span
                    className="flex h-full w-full cursor-pointer flex-col items-center justify-center rounded-xl p-2 text-2xl font-medium text-blue-700"
                  >
                    <Plus className="w-12 h-12 mb-5" />
                    <div>Create Workspace</div>
                  </span>
                </div>
              )}
              {cardRows.map(({ header, product }, index: number) => header !== undefined ? (
                <div
                  key={`header-${header}`}
                  className="col-span-full text-sm font-semibold uppercase tracking-wide text-slate-500"
                >
                  {header}
                </div>
              ) : (
                <div
                  key={index}
                  onClick={() => onSelectWorkspace(product)}
                  className="overflow-hidden flex min-h-[220px] cursor-pointer flex-col justify-between rounded-xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
                >
                  <div className="px-5 py-6">
                    <div className="flex justify-between">
                      <div>
                        <div className="text-lg font-semibold">
                          {product.name}
                        </div>
                        <div className="text-sm text-[#6B7280] min-h-10">
                          {product.description}
                        </div>
                      </div>
                      <div>
                        {product.avatar_url && (
                          <Avatar
                            size={40}
                            src={product.avatar_url}
                            icon={!product.avatar_url}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-slate-100 px-5 py-4">
                    <div className="flex gap-3">
                      <div className='flex items-center text-[#6B7280] gap-1'>
                        {product.user_count}
                        <UsersRound className='w-4 ' />
                      </div>
                      <div className='flex items-center text-[#6B7280] gap-1'>
                        {product.vuln_count}
                        <Bug className='w-4 ' />
                      </div>
                      <div className='flex items-center text-[#6B7280] gap-1'>
                        {product.asset_count}
                        <Server className='w-4 ' />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className='text-sm text-[#6B7280]'>
                        {formatDate(product.created_at)}
                      </span>
                      {/* `can_delete` is computed per workspace by the backend — never re-derive
                          it here from roles, it would drift the moment the rule changes. */}
                      <Tooltip
                        title={
                          product.can_delete
                            ? 'Delete workspace'
                            : `Only a workspace admin can delete this workspace${
                              product.current_user_role
                                ? ` — your role is ${product.current_user_role}`
                                : ''
                            }`
                        }
                      >
                        {/* A disabled antd Button swallows mouse events, so the span is what
                            the Tooltip hangs off — otherwise the explanation never shows. */}
                        <span
                          className={product.can_delete ? '' : 'cursor-not-allowed'}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            disabled={!product.can_delete}
                            className={product.can_delete ? '' : 'pointer-events-none'}
                            icon={<Trash2 className="w-4" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setWorkspaceToDelete(product);
                            }}
                          />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Deleting the last workspace a user belongs to is a reachable state — the
              backend does not backfill a replacement — so it needs a real destination
              rather than an empty grid. */}
          {!loading && loadError && (
            <Alert
              type="error"
              showIcon
              message="We could not load your workspaces."
              description="Your access may still be available. Please try again."
              action={<Button
                onClick={() => fetchWorkspaces(query)}
              >Retry</Button>} /
            >
          )}
          {!loading && !loadError && workspaceRows.length === 0 && (
            <div className="mt-10 flex justify-center">
              <Empty
                description={
                  query
                    ? `No workspace matches "${query}".`
                    : emailVerificationRequired
                      ? 'Verify your email address with your sign-in provider, then sign out and sign in again to finish setting up your organisation and first workspace.'
                      : invitations.length > 0
                        ? 'Accept an invitation above to join your first workspace.'
                        : `Your account is ready. Ask your organisation administrator to invite ${email} to the organisation, then refresh below.`
                }
              >
                <Button
                  onClick={() => {
                    fetchWorkspaces();
                    fetchInvitations();
                  }}
                >
                  Refresh access
                </Button>
              </Empty>
            </div>
          )}
        </div>
      </main>

      <DeleteWorkspaceModal
        open={workspaceToDelete !== null}
        workspace={workspaceToDelete}
        onCancel={() => setWorkspaceToDelete(null)}
        onDeleted={onWorkspaceDeleted}
        onUnavailable={onWorkspaceUnavailable}
      />
    </div>
  );
}
